import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import warehouseBg from "@assets/generated_images/modern_industrial_warehouse_interior_with_warm_lighting.png";
import { ArrowRight, Building2, Lock, Mail, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

export default function Login() {
  const { t } = useTranslation();
  const { login, user, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  
  useEffect(() => {
    if (!isLoading && user) {
      let next = "/dashboard";
      try {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get("next");
        if (raw && raw.startsWith("/") && !raw.startsWith("//")) {
          next = raw;
        }
      } catch {}
      setLocation(next);
    }
  }, [user, isLoading, setLocation]);
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Show loading spinner while checking authentication status
  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
    } catch (error: any) {
      setLoading(false);
      toast({
        title: t("authPages.authFailedTitle"),
        description: error.message || t("authPages.authFailedDesc"),
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-5 overflow-hidden bg-background">
      
      {/* Left Side: Form Area (2 cols) */}
      <div className="col-span-5 lg:col-span-2 relative flex flex-col justify-center p-6 sm:p-8 md:p-12 lg:p-16 z-10">
        
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-30 -z-10" />

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="w-full max-w-sm mx-auto space-y-10"
        >
          {/* Minimal Header */}
          <div className="space-y-6">
            <div className="h-14 w-14 bg-primary rounded-2xl flex items-center justify-center shadow-lg shadow-primary/20">
               <Building2 className="h-7 w-7 text-primary-foreground" />
            </div>
            <div className="space-y-2">
              <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground" data-testid="text-portal-title">
                {t("authPages.portalTitle")}
              </h1>
              <p className="text-muted-foreground text-base font-light leading-relaxed">
                {t("authPages.portalSubtitle")}
              </p>
            </div>
          </div>

          {/* Modern Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ps-1">{t("authPages.emailLabel")}</Label>
                <div className="relative group">
                  <Mail className="absolute start-3 top-3 h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <Input 
                    type="email" 
                    placeholder={t("authPages.emailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 ps-10 bg-background border-muted-foreground/20 hover:border-primary/50 focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all rounded-lg"
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between ps-1">
                  <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{t("authPages.passwordLabel")}</Label>
                  <Link href="/forgot-password" className="text-[10px] text-primary cursor-pointer hover:underline" data-testid="link-forgot-password">{t("authPages.forgotPasswordLink")}</Link>
                </div>
                <div className="relative group">
                  <Lock className="absolute start-3 top-3 h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <Input 
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 ps-10 pe-10 bg-background border-muted-foreground/20 hover:border-primary/50 focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all rounded-lg"
                    required
                    data-testid="input-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute end-3 top-3 h-5 w-5 text-muted-foreground hover:text-primary transition-colors"
                    data-testid="button-toggle-password"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full h-12 text-base font-medium bg-gradient-to-r from-primary to-blue-600 hover:from-blue-600 hover:to-primary text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 rounded-lg"
              data-testid="button-signin"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t("authPages.authenticating")}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {t("authPages.signIn")} <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
            
          </form>

          {/* Minimal Footer */}
          <div className="pt-6 flex items-center justify-between border-t border-border/40">
             <p className="text-xs text-muted-foreground font-medium tracking-wide">
               {t("authPages.secureAccess")}
             </p>
             <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
                <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">{t("authPages.systemOnline")}</span>
             </div>
          </div>

        </motion.div>
      </div>

      {/* Right Side: Image Area (3 cols) */}
      <div className="hidden lg:block col-span-3 relative overflow-hidden bg-black">
        
        {/* Animated Image Background with Parallax effect simulated by scale */}
        <motion.div 
           initial={{ scale: 1.1, opacity: 0 }}
           animate={{ scale: 1, opacity: 1 }}
           transition={{ duration: 1.5, ease: "easeOut" }}
           className="absolute inset-0"
        >
           <img 
            src={warehouseBg} 
            alt={t("authPages.warehouseAlt")} 
            className="w-full h-full object-cover opacity-60"
          />
        </motion.div>
        
        {/* Modern Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-black/20 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />

        {/* Content Overlay */}
        <div className="absolute bottom-0 start-0 w-full p-16 z-20">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="max-w-2xl space-y-8"
          >
            <h2 className="text-6xl font-heading font-bold text-white leading-[1.05] tracking-tight">
              {t("authPages.enterprise")} <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-200">
                {t("authPages.logisticsControl")}
              </span>
            </h2>
            
            <div className="flex flex-col gap-6 border-s-2 border-blue-500/50 ps-6">
              <p className="text-xl text-gray-300 font-light leading-relaxed max-w-lg">
                {t("authPages.heroDescription")}
              </p>
            </div>
            
            {/* Glass Stats */}
            <div className="flex gap-4 pt-4">
               <div className="px-6 py-4 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 transition-colors cursor-default group">
                 <div className="text-3xl font-heading font-bold text-white group-hover:text-blue-400 transition-colors">99.8%</div>
                 <div className="text-xs text-gray-400 uppercase tracking-wider font-medium mt-1">{t("authPages.operationalUptime")}</div>
               </div>
               <div className="px-6 py-4 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 transition-colors cursor-default group">
                 <div className="text-3xl font-heading font-bold text-white group-hover:text-blue-400 transition-colors">24/7</div>
                 <div className="text-xs text-gray-400 uppercase tracking-wider font-medium mt-1">{t("authPages.liveMonitoring")}</div>
               </div>
            </div>

          </motion.div>
        </div>
      </div>
    </div>
  );
}
